import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import Softphone from '../../components/ems/Softphone';
import { usePBX } from '../../context/PBXContext';
import { Phone, Wifi, WifiOff, Mic, MicOff, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function ParticipantPhonePage() {
  const pbx = usePBX();
  const myPhone = pbx?.myPhone;
  const exerciseId = pbx?.exerciseId;
  const connected = !!myPhone;

  const [micStatus, setMicStatus] = useState('unknown'); // unknown, granted, denied, prompt

  useEffect(() => {
    // Check microphone permission status
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'microphone' }).then(result => {
        setMicStatus(result.state);
        result.onchange = () => setMicStatus(result.state);
      }).catch(() => setMicStatus('unknown'));
    }
  }, []);

  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setMicStatus('granted');
    } catch {
      setMicStatus('denied');
    }
  };

  return (
    <DashboardLayout title="Phone">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Status banner */}
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
          connected
            ? 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400'
            : 'bg-muted/50 border-border text-muted-foreground'
        }`} data-testid="phone-status-banner">
          {connected
            ? <Wifi className="w-4 h-4 flex-shrink-0" />
            : <WifiOff className="w-4 h-4 flex-shrink-0" />}
          <div className="text-sm">
            {connected ? (
              <span>
                Connected — Extension <strong>{myPhone?.extension}</strong>
                {myPhone?.role_name && ` (${myPhone.role_name})`}
              </span>
            ) : exerciseId ? (
              <span>Loading phone assignment...</span>
            ) : (
              <span>Not assigned to any active exercise. Contact your administrator.</span>
            )}
          </div>
        </div>

        {/* Mic permission banner */}
        {micStatus !== 'granted' && (
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
            micStatus === 'denied'
              ? 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
              : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-700 dark:text-yellow-400'
          }`} data-testid="mic-permission-banner">
            {micStatus === 'denied' ? (
              <MicOff className="w-4 h-4 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            )}
            <div className="flex-1 text-sm">
              {micStatus === 'denied' ? (
                <span>Microphone access was denied. Please enable it in your browser settings to use the phone.</span>
              ) : (
                <span>Microphone permission is required for voice calls.</span>
              )}
            </div>
            {micStatus !== 'denied' && (
              <Button size="sm" variant="outline" onClick={requestMicPermission} data-testid="request-mic-btn">
                <Mic className="w-3 h-3 mr-1" />
                Enable Mic
              </Button>
            )}
          </div>
        )}

        {/* Full softphone */}
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <Phone className="w-4 h-4 text-primary" />
            <span className="font-semibold text-sm">Softphone</span>
          </div>
          <div className="p-4">
            <Softphone embedded={false} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
